import React, { useEffect, useState } from 'react';
import { Attendance, AttendanceResponse } from '../../interfaces/attendance.interface';
import { AttendanceService } from '../../services/attendance.service';
import { LeaveApplicationResponse, LeaveStatus } from '../../interfaces/leave.interface';
import { LeaveService } from '../../services/leave.service';
import { HolidayResponse } from '../../interfaces/holiday.interface';
import { HolidayService } from '../../services/holiday.service';
import { UserService } from '../../services/user.service';
import { UserType } from '../../interfaces/userType.enum';
import {
    Box, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Typography, Button, Grid, TextField, Autocomplete,
    IconButton, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions
} from '@mui/material';
import { useShiftLookup } from '../../hooks/useShiftLookup';
import { Edit } from '@mui/icons-material';
import { format, isWithinInterval, parseISO, eachDayOfInterval, startOfDay, endOfDay, subDays } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import { displayUsername } from '../../utils/userUtils';
import { InstitutionSettingsService } from '../../services/institutionSettings.service';
import { WeekDay } from '../../interfaces/institutionSettings.interface';
import { AuthService } from '../../services/auth.service';
import { useTranslation } from 'react-i18next';
import { InstanceStatus, isInstanceStatus } from '../shifts/instanceStatus';

const JS_DAY_TO_NAME: WeekDay[] = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface DayStatus {
    kind: 'present' | 'absent' | 'holiday' | 'leave';
    label: string;
}

interface User {
    id: string;
    username: string;
    type: UserType;
}

const EmployeeAttendanceList: React.FC = () => {
    const [employees, setEmployees] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userInput, setUserInput] = useState('');
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [attendanceData, setAttendanceData] = useState<AttendanceResponse | null>(null);
    const [leaveData, setLeaveData] = useState<LeaveApplicationResponse | null>(null);
    const [holidayData, setHolidayData] = useState<HolidayResponse | null>(null);
    const [dateRange, setDateRange] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingAttendance, setEditingAttendance] = useState<Attendance | null>(null);
    const [editStartTime, setEditStartTime] = useState('');
    const [editEndTime, setEditEndTime] = useState('');
    const [saving, setSaving] = useState(false);
    const [weekendDays, setWeekendDays] = useState<WeekDay[]>(['FRIDAY', 'SATURDAY']);
    const [selectedShiftId, setSelectedShiftId] = useState<string>('');
    const toast = useToast();
    const { t } = useTranslation(['attendance', 'common', 'shifts']);
    const { shifts } = useShiftLookup();

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const institutionId = AuthService.getSelectedInstitution();
                const [leaveResponse, holidayResponse, settings] = await Promise.all([
                    LeaveService.getAllApplicationsByInstitution(0, 500),
                    HolidayService.getHolidaysByInstitution(),
                    institutionId ? InstitutionSettingsService.getSettings(institutionId) : Promise.resolve(null),
                ]);
                setLeaveData(leaveResponse);
                setHolidayData(holidayResponse);
                if (settings?.weekendDays && settings.weekendDays.length > 0) {
                    setWeekendDays(settings.weekendDays);
                }
            } catch (err) {
                toast.showApiError(err, t('fetchFailed'));
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        const search = userInput.trim();
        const handle = setTimeout(async () => {
            setUserSearchLoading(true);
            try {
                // Empty search loads the first 20 employees so the list is populated by default;
                // a search returns up to the first 20 matches.
                const res = await UserService.getInstitutionUsers(0, 20, undefined, search || undefined);
                setEmployees(res.content);
            } catch (err) {
                toast.showApiError(err, t('employeeAttendance.searchFailed'));
            } finally {
                setUserSearchLoading(false);
            }
        }, search ? 300 : 0);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInput]);

    useEffect(() => {
        if (startDate && endDate) {
            const range = eachDayOfInterval({
                start: startOfDay(startDate),
                end: endOfDay(endDate)
            });
            setDateRange(range.map(d => format(d, 'yyyy-MM-dd')).reverse());
        }
    }, [startDate, endDate]);

    const fetchAttendance = async () => {
        if (!selectedUser) {
            toast.showError(t('employeeAttendance.selectEmployee'));
            return;
        }
        try {
            setLoading(true);
            const data = await AttendanceService.getEmployeeAttendanceList(selectedUser.id, startDate, endDate);
            setAttendanceData(data);
        } catch (err) {
            toast.showApiError(err, t('employeeAttendance.fetchAttendanceFailed'));
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (dateString: string) => {
        if (!dateString) return '-';
        return format(parseISO(dateString), 'HH:mm');
    };

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const formatDuration = (startStr: string, endStr: string): string => {
        if (!startStr || !endStr) return '-';
        const diffMs = parseISO(endStr).getTime() - parseISO(startStr).getTime();
        if (diffMs <= 0) return '-';
        const totalMinutes = Math.floor(diffMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return t('duration', { hours, minutes });
    };

    const getStatus = (date: string): DayStatus => {
        const dayName = JS_DAY_TO_NAME[parseISO(date).getDay()];
        if (weekendDays.includes(dayName)) {
            return { kind: 'holiday', label: t('dayStatus.weekend') };
        }

        if (attendanceData?.content.some(a => a.date === date)) {
            return { kind: 'present', label: t('dayStatus.present') };
        }

        const leaveApplication = leaveData?.content.find(leave =>
            leave.userId === selectedUser?.id &&
            leave.leaveStatus === LeaveStatus.Approved &&
            isWithinInterval(parseISO(date), {
                start: parseISO(leave.startDate),
                end: parseISO(leave.endDate)
            })
        );
        if (leaveApplication) {
            return { kind: 'leave', label: leaveApplication.leaveType };
        }

        const holiday = holidayData?.content.find(h =>
            isWithinInterval(parseISO(date), {
                start: parseISO(h.startDate),
                end: parseISO(h.endDate)
            })
        );
        if (holiday) {
            return { kind: 'holiday', label: t('dayStatus.holiday', { name: holiday.name }) };
        }

        return { kind: 'absent', label: t('dayStatus.absent') };
    };

    const handleOpenEditDialog = (attendance: Attendance) => {
        setEditingAttendance(attendance);
        setEditStartTime(format(parseISO(attendance.startTime), 'HH:mm'));
        setEditEndTime(format(parseISO(attendance.endTime), 'HH:mm'));
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingAttendance(null);
    };

    const handleSaveEdit = async () => {
        if (!editingAttendance) return;
        try {
            setSaving(true);
            const date = editingAttendance.date;
            const startTime = new Date(`${date}T${editStartTime}:00`).toISOString();
            const endTime = new Date(`${date}T${editEndTime}:00`).toISOString();
            await AttendanceService.updateAttendance(editingAttendance.id, startTime, endTime);
            toast.showSuccess(t('updateSuccess'));
            handleCloseEditDialog();
            await fetchAttendance();
        } catch (err) {
            toast.showApiError(err, t('updateFailed'));
        } finally {
            setSaving(false);
        }
    };

    const getAttendanceByDate = (date: string) =>
        attendanceData?.content.find(a => a.date === date);

    const getStatusCellStyle = (status: DayStatus['kind']) => {
        const baseStyle = { ...tableCellStyle };
        if (status === 'present') return { ...baseStyle, backgroundColor: '#e6ffe6' };
        if (status === 'absent') return { ...baseStyle, backgroundColor: '#ffebeb' };
        if (status === 'holiday') return { ...baseStyle, backgroundColor: '#ffffcc' };
        return baseStyle;
    };

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
                {t('employeeAttendance.title')}
            </Typography>

            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                <Grid>
                    <Autocomplete
                        sx={{ minWidth: 220 }}
                        options={employees}
                        getOptionLabel={(option) => displayUsername(option.username)}
                        value={selectedUser}
                        onChange={(_, newValue) => {
                            setSelectedUser(newValue);
                            setAttendanceData(null);
                        }}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input') {
                                setUserInput(value);
                            } else if (reason === 'clear') {
                                setUserInput('');
                            }
                        }}
                        filterOptions={(x) => x}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        loading={userSearchLoading}
                        noOptionsText={userSearchLoading ? t('common:status.loading') : t('employeeAttendance.noEmployees')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('common:fields.employee')} placeholder={t('employeeAttendance.searchPlaceholder')} />
                        )}
                    />
                </Grid>
                <Grid>
                    <TextField
                        label={t('startDate')}
                        type="date"
                        value={format(startDate, 'yyyy-MM-dd')}
                        onChange={(e) => setStartDate(new Date(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid>
                    <TextField
                        label={t('endDate')}
                        type="date"
                        value={format(endDate, 'yyyy-MM-dd')}
                        onChange={(e) => setEndDate(new Date(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid>
                    <FormControl sx={{ minWidth: 160 }}>
                        <InputLabel id="shift-filter-label">{t('shiftFilter.label')}</InputLabel>
                        <Select
                            labelId="shift-filter-label"
                            value={selectedShiftId}
                            label={t('shiftFilter.label')}
                            onChange={(e) => setSelectedShiftId(e.target.value)}
                        >
                            <MenuItem value="">{t('shiftFilter.all')}</MenuItem>
                            {shifts.filter(s => s.isActive).map(s => (
                                <MenuItem key={s.id} value={s.id}>{s.code} – {s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={fetchAttendance}
                        disabled={loading}
                        sx={{ mt: { xs: 0, md: 1 } }}
                    >
                        {t('applyFilters')}
                    </Button>
                </Grid>
            </Grid>

            {attendanceData && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={tableHeaderStyle}>{t('common:fields.date')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('columns.day')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('columns.startTime')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('columns.endTime')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('columns.totalTime')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('common:fields.status')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('columns.shift')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('columns.instanceStatus')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('columns.late')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('columns.ot')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('common:fields.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dateRange
                                .filter(date => {
                                    if (!selectedShiftId) return true;
                                    const attendance = getAttendanceByDate(date);
                                    return attendance?.shiftId === selectedShiftId;
                                })
                                .map((date) => {
                                    const attendance = getAttendanceByDate(date);
                                    const status = getStatus(date);
                                    return (
                                        <TableRow key={date}>
                                            <TableCell sx={tableCellStyle}>{formatDate(date)}</TableCell>
                                            <TableCell sx={tableCellStyle}>{t(`common:weekdays.${parseISO(date).getDay() as WeekdayIndex}`)}</TableCell>
                                            <TableCell sx={tableCellStyle}>
                                                {attendance ? formatTime(attendance.startTime) : '-'}
                                            </TableCell>
                                            <TableCell sx={tableCellStyle}>
                                                {attendance ? formatTime(attendance.endTime) : '-'}
                                            </TableCell>
                                            <TableCell sx={tableCellStyle}>
                                                {attendance ? formatDuration(attendance.startTime, attendance.endTime) : '-'}
                                            </TableCell>
                                            <TableCell sx={getStatusCellStyle(status.kind)}>{status.label}</TableCell>
                                            <TableCell sx={tableCellStyle}>
                                                {attendance?.shiftCode
                                                    ? <Chip label={attendance.shiftCode} size="small" />
                                                    : '-'}
                                            </TableCell>
                                            <TableCell sx={tableCellStyle}>
                                                {isInstanceStatus(attendance?.instanceStatus)
                                                ? t(`shifts:instanceStatus.${attendance!.instanceStatus as InstanceStatus}`)
                                                : attendance?.instanceStatus ?? '-'}
                                            </TableCell>
                                            <TableCell sx={tableCellStyle}>
                                                {attendance?.isLate
                                                    ? <Chip label={t('minutesShort', { minutes: attendance.lateMinutes })} size="small" color="error" />
                                                    : '-'}
                                            </TableCell>
                                            <TableCell sx={tableCellStyle}>
                                                {attendance?.overtimeMinutes && attendance.overtimeMinutes > 0
                                                    ? <Chip label={t('minutesShort', { minutes: attendance.overtimeMinutes })} size="small" />
                                                    : '-'}
                                            </TableCell>
                                            <TableCell sx={tableCellStyle}>
                                                {attendance && (
                                                    <Tooltip title={t('editAttendance')} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenEditDialog(attendance)}
                                                            color="primary"
                                                            aria-label={t('editAttendance')}
                                                        >
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!attendanceData && !loading && (
                <Typography color="text.secondary">
                    {t('employeeAttendance.hint')}
                </Typography>
            )}

            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{t('editAttendance')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label={t('common:fields.date')}
                            value={editingAttendance ? format(parseISO(editingAttendance.date), 'dd-MM-yyyy') : ''}
                            disabled
                            fullWidth
                        />
                        <TextField
                            label={t('columns.startTime')}
                            type="time"
                            value={editStartTime}
                            onChange={(e) => setEditStartTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label={t('columns.endTime')}
                            type="time"
                            value={editEndTime}
                            onChange={(e) => setEditEndTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog} disabled={saving}>{t('common:actions.cancel')}</Button>
                    <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
                        {saving ? t('saving') : t('common:actions.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default EmployeeAttendanceList;
